import { FC, useMemo } from 'react';
import { useTeamDemandReportQuery } from 'TeamModule/query/hooks/useTeamDemandReportQuery';
import {
  BudgetAggregate,
  BudgetAggregateIntervalType,
} from 'BudgetModule/constants/budgetAggregate';
import { getFormattedNextThirtyDaysRange, dateRangeDefaultDateFormat } from '@constants';
import { Bar } from 'react-chartjs-2';
import { conditionallyRenderElement, formatChartDateRange } from 'utils';
import { Box } from '@mui/material';
import {
  capacityConfig,
  holidayConfig,
  nonTentativePlannedConfig,
  ptoConfig,
  tentativePlannedConfig,
} from 'BudgetModule/constants/budget';
import { transformBarChartData } from 'IntegrationModule/utils/barChart';
import { defaultChartData } from '@constants/chart';

const options = {
  responsive: true,
  scales: {
    x: {
      stacked: true,
    },
    y: {
      stacked: true,
      ticks: {
        callback: function (value: string | number) {
          return `${value}h`;
        },
      },
    },
  },
};

/**
 * Spec:
 *
 * Only for the next 30 days
 */
const { startDate, endDate } = getFormattedNextThirtyDaysRange(dateRangeDefaultDateFormat);

export const TeamUtilizationBarChartContainer: FC<{ teamId: number }> = ({ teamId }) => {
  const { isLoadingTeamDemandReport, teamDemandReportData, teamDemandReportError } =
    useTeamDemandReportQuery({
      team_id: teamId,
      data_type: BudgetAggregate.Team,
      all: true,
      start_date: startDate,
      end_date: endDate,
      interval_amount: '1',
      interval_type: BudgetAggregateIntervalType.Week,
      show_holidays: true,
      show_pto: true,
    });

  const { barChartData } = useMemo(() => {
    if (teamDemandReportData) {
      const orderedReportData = teamDemandReportData.records.map((item) => ({
        xAxisLabel: formatChartDateRange({
          startDate: item.start_date,
          endDate: item.end_date,
        }),
        bars: [
          {
            ...capacityConfig,
            value: item.capacity,
          },
          {
            ...tentativePlannedConfig,
            value: +item.demand_tentative,
          },
          {
            ...nonTentativePlannedConfig,
            value: +item.demand_non_tentative,
          },
          {
            ...holidayConfig,
            value: item.holiday_hours,
          },
          {
            ...ptoConfig,
            value: item.pto_hours,
          },
        ],
      }));

      const barChartData = transformBarChartData({
        orderedChartData: orderedReportData,
      });

      return { barChartData };
    }

    return { barChartData: defaultChartData };
  }, [teamDemandReportData]);

  return (
    <Box>
      <h3>Utilization for the next 30 days</h3>
      {conditionallyRenderElement({
        isLoading: isLoadingTeamDemandReport,
        error: teamDemandReportError,
        content: <Bar data={barChartData} options={options} />,
      })}
    </Box>
  );
};
